// src/pages/DemoConsole.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./DemoConsole.css";
import "./DemoConsoleExtras.css";
import "./DemoConsolePro.css";
import "./DemoConsoleV2.css";

import logo from "../assets/panelzero-icon.png";
import {
  TENANT,
  USER,
  STATS,
  DENIALS,
  STATUS_META,
  FILTERS,
  EOB_SNIPPET,
  APPEAL_LETTER,
  CLINICAL_NARRATIVE,
  OP_NOTE_EXCERPT,
  LCD_CITATION,
  ACTIVITY_FEED,
  P2P,
  IMAGING,
  INTEGRATIONS,
  SETTINGS,
  VOICE_CALLS,
  VOICE_STATUS_META,
  FAXES,
  FAX_STATUS_META,
} from "../lib/demoData";
import { setDemoAuthed } from "./DemoLogin";

/* ──────────────────────────────────────────────────────────────
   Utilities
   ────────────────────────────────────────────────────────────── */
const fmtMoney = (n) =>
  "$" + n.toLocaleString("en-US", { maximumFractionDigits: 0 });

function useCountUp(target, { duration = 1200, decimals = 0 } = {}) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(target * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, decimals]);
  return v;
}

/* ──────────────────────────────────────────────────────────────
   Sidebar
   ────────────────────────────────────────────────────────────── */
const NAV = [
  { id: "denials",       label: "Denials",       icon: IconQueue,   count: STATS.openDenials },
  { id: "appeals",       label: "Appeals",       icon: IconMail,    count: 38 },
  { id: "faxes",         label: "Faxes",         icon: IconPrinter, count: 47 },
  { id: "voice",         label: "Voice calls",   icon: IconPhone,   count: 23 },
  { id: "analytics",     label: "Analytics",     icon: IconChart },
  { id: "integrations",  label: "Integrations",  icon: IconPlug,    badge: "Epic" },
  { id: "settings",      label: "Settings",      icon: IconGear },
];

function Sidebar({ view, onChange, onLogout }) {
  return (
    <aside className="pzd-side">
      <div className="pzd-brand">
        <img src={logo} alt="" />
        <div>
          <div className="pzd-brandname">PanelZero</div>
          <div className="pzd-tenant">{TENANT.name.split(" · ")[0]}</div>
        </div>
      </div>

      <nav className="pzd-nav">
        {NAV.map((n) => {
          const Icon = n.icon;
          const active = view === n.id;
          return (
            <button
              key={n.id}
              className={`pzd-navitem ${active ? "is-active" : ""}`}
              onClick={() => onChange(n.id)}
              title={n.label}
            >
              <span className="pzd-navicon"><Icon /></span>
              <span className="pzd-navlabel">{n.label}</span>
              {n.count != null && <span className="pzd-navcount">{n.count}</span>}
              {n.badge && <span className="pzd-navbadge">{n.badge}</span>}
            </button>
          );
        })}
      </nav>

      <div className="pzd-side-foot">
        <div className="pzd-user">
          <div className="pzd-avatar">{USER.initials}</div>
          <div className="pzd-usertext">
            <div className="pzd-username">{USER.name}</div>
            <div className="pzd-userrole">{USER.role}</div>
          </div>
        </div>
        <button className="pzd-logout" onClick={onLogout} title="Sign out">Sign out</button>
      </div>
    </aside>
  );
}

/* ──────────────────────────────────────────────────────────────
   Top stat strip
   ────────────────────────────────────────────────────────────── */
function StatStrip() {
  const recovered = useCountUp(STATS.recoveredYTD, { duration: 1400 });
  const open = useCountUp(STATS.openDenials, { duration: 1000 });
  const overturn = useCountUp(STATS.overturnRate * 100, { duration: 1200 });
  const tat = useCountUp(STATS.avgTurnaroundHours, { duration: 1200 });
  return (
    <div className="pzd-statstrip">
      <div className="pzd-stat">
        <div className="pzd-stat-label">Recovered YTD</div>
        <div className="pzd-stat-value">{fmtMoney(Math.round(recovered))}</div>
        <div className="pzd-stat-trend pzd-stat-trend--up">▲ {(STATS.recoveredTrend*100).toFixed(0)}% vs. last 90d</div>
      </div>
      <div className="pzd-stat">
        <div className="pzd-stat-label">Open denials</div>
        <div className="pzd-stat-value">{Math.round(open)}</div>
        <div className="pzd-stat-trend">127 this week</div>
      </div>
      <div className="pzd-stat">
        <div className="pzd-stat-label">Overturn rate</div>
        <div className="pzd-stat-value">{overturn.toFixed(0)}<small>%</small></div>
        <div className="pzd-stat-trend pzd-stat-trend--up">▲ {(STATS.overturnTrend*100).toFixed(0)} pts</div>
      </div>
      <div className="pzd-stat">
        <div className="pzd-stat-label">Avg turnaround</div>
        <div className="pzd-stat-value">{tat.toFixed(1)}<small>h</small></div>
        <div className="pzd-stat-trend">down from 3.2 days</div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   Denial queue
   ────────────────────────────────────────────────────────────── */
function DenialRow({ d, active, onClick, delay }) {
  const meta = STATUS_META[d.status] || STATUS_META.queued;
  return (
    <button
      type="button"
      className={`pzd-row ${active ? "is-active" : ""}`}
      style={{ "--pzd-delay": `${delay}ms` }}
      onClick={onClick}
    >
      <div className="pzd-row-id">
        <div className="pzd-row-claim">{d.claimId}</div>
        <div className="pzd-row-enc">{d.encounter}</div>
      </div>
      <div className="pzd-row-patient">
        <div className="pzd-row-patientname">{d.patient.name}</div>
        <div className="pzd-row-patientmrn">MRN {d.patient.mrn}</div>
      </div>
      <div className="pzd-row-payer">
        <div>{d.payer}</div>
        <div className="pzd-row-plan">{d.plan}</div>
      </div>
      <div className="pzd-row-service">
        <div>CPT {d.cpt} · {d.dx}</div>
        <div className="pzd-row-serviceSub">{d.service}</div>
      </div>
      <div className="pzd-row-denial">
        <div className="pzd-row-carc">{d.carc}</div>
        <div className="pzd-row-reason">{d.reasonShort}</div>
      </div>
      <div className="pzd-row-win">
        <div className="pzd-row-winnum">{Math.round(d.winProbability * 100)}%</div>
        <div className="pzd-row-winbar">
          <span style={{ width: `${Math.round(d.winProbability * 100)}%` }} />
        </div>
      </div>
      <div className="pzd-row-amount">{fmtMoney(d.amount)}</div>
      <div className="pzd-row-status">
        <span className={`pzd-statuschip pzd-statuschip--${meta.tone}`}>
          <span className="pzd-statusdot" style={{ background: meta.dot }} />
          {meta.label}
        </span>
      </div>
    </button>
  );
}

function DenialQueue({ onSelect, selectedId }) {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const rows = useMemo(() => {
    return DENIALS.filter((d) => {
      if (filter !== "all" && d.status !== filter) return false;
      if (q && !`${d.claimId} ${d.patient.name} ${d.payer} ${d.cpt} ${d.service}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [filter, q]);

  return (
    <section className="pzd-main">
      <header className="pzd-main-head">
        <div>
          <h1 className="pzd-h1">Denial queue</h1>
          <p className="pzd-h1-sub">
            Auto-drafted by PanelZero from your 835 remits and Epic FHIR feed. Review, edit, submit.
          </p>
        </div>
        <div className="pzd-searchwrap">
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <circle cx="11" cy="11" r="6" fill="none" stroke="currentColor" strokeWidth="1.8" />
            <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            placeholder="Search claim, patient, payer, CPT…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </header>

      <StatStrip />

      <div className="pzd-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            className={`pzd-filter ${filter === f.id ? "is-active" : ""}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
        <span className="pzd-filter-count">{rows.length} of {DENIALS.length}</span>
      </div>

      <div className="pzd-tablehead">
        <div>Claim</div>
        <div>Patient</div>
        <div>Payer</div>
        <div>Service</div>
        <div>Denial reason</div>
        <div>Win %</div>
        <div style={{ textAlign: "right" }}>At risk</div>
        <div>Status</div>
      </div>

      <div className="pzd-rows">
        {rows.map((d, i) => (
          <DenialRow
            key={d.id}
            d={d}
            active={selectedId === d.id}
            onClick={() => onSelect(d)}
            delay={i * 60}
          />
        ))}
        {rows.length === 0 && <div className="pzd-empty">No denials match.</div>}
      </div>

      <aside className="pzd-activity">
        <div className="pzd-activity-title">Activity · last 48h</div>
        {ACTIVITY_FEED.map((a, i) => (
          <div className="pzd-activity-row" key={i}>
            <div className="pzd-activity-dot" />
            <div className="pzd-activity-what"><strong>{a.who}</strong> — {a.what}</div>
            <div className="pzd-activity-at">{a.at}</div>
          </div>
        ))}
      </aside>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────
   Denial detail drawer + appeal package PDF preview
   ────────────────────────────────────────────────────────────── */
function DenialDrawer({ denial, onClose, autoPreview, onNavigate }) {
  const p2p = denial ? P2P[denial.id] : null;
  const [tab, setTab] = useState("letter");
  const [sent, setSent] = useState(false);
  const [pdfMax, setPdfMax] = useState(false);
  const [pdfPage, setPdfPage] = useState(0);
  const [autoPlaying, setAutoPlaying] = useState(false);

  useEffect(() => {
    setTab("letter");
    setSent(false);
    setPdfMax(false);
    setPdfPage(0);
    setAutoPlaying(false);
  }, [denial?.id]);

  // Auto-preview: on first open of the featured denial, hop to the package tab and flip pages.
  useEffect(() => {
    if (!denial || !autoPreview) return;
    const openTimeout = setTimeout(() => {
      setTab("package");
      setAutoPlaying(true);
    }, 900);
    return () => clearTimeout(openTimeout);
  }, [denial, autoPreview]);

  useEffect(() => {
    if (!autoPlaying) return;
    const totalPages = 3 + EXHIBITS.length;
    const id = setInterval(() => {
      setPdfPage((p) => {
        if (p + 1 >= totalPages) {
          setAutoPlaying(false);
          return p;
        }
        return p + 1;
      });
    }, 1800);
    return () => clearInterval(id);
  }, [autoPlaying]);

  const cancelAuto = () => setAutoPlaying(false);

  // Live-typing appeal letter
  const [typed, setTyped] = useState("");
  useEffect(() => {
    if (!denial) return;
    if (!denial.featured) { setTyped(APPEAL_LETTER); return; }
    setTyped("");
    let i = 0;
    const id = setInterval(() => {
      i += 6;
      setTyped(APPEAL_LETTER.slice(0, i));
      if (i >= APPEAL_LETTER.length) clearInterval(id);
    }, 14);
    return () => clearInterval(id);
  }, [denial]);

  if (!denial) return null;
  const meta = STATUS_META[denial.status] || STATUS_META.queued;
  const p2pUplift = p2p ? Math.round((p2p.winWith - p2p.winWithout) * 100) : 0;

  return (
    <>
      <div className="pzd-scrim" onClick={onClose} />
      <aside className={`pzd-drawer ${pdfMax ? "is-pdfmax" : ""}`} role="dialog" aria-label="Denial detail">
        <header className="pzd-drawer-head">
          <div className="pzd-drawer-left">
            <div className="pzd-drawer-claim">{denial.claimId}</div>
            <span className={`pzd-statuschip pzd-statuschip--${meta.tone}`}>
              <span className="pzd-statusdot" style={{ background: meta.dot }} />
              {meta.label}
            </span>
          </div>
          <button className="pzd-drawer-close" onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className="pzd-drawer-summary">
          <div className="pzd-ds-row">
            <div className="pzd-ds-cell">
              <div className="pzd-ds-label">Patient</div>
              <div className="pzd-ds-value">{denial.patient.name} · MRN {denial.patient.mrn}</div>
              <div className="pzd-ds-sub">{denial.patient.sex} · DOB {denial.patient.dob}</div>
            </div>
            <div className="pzd-ds-cell">
              <div className="pzd-ds-label">Payer</div>
              <div className="pzd-ds-value">{denial.payer}</div>
              <div className="pzd-ds-sub">{denial.plan}</div>
            </div>
            <div className="pzd-ds-cell">
              <div className="pzd-ds-label">Service</div>
              <div className="pzd-ds-value">CPT {denial.cpt} · {denial.dx}</div>
              <div className="pzd-ds-sub">{denial.service}</div>
            </div>
            <div className="pzd-ds-cell">
              <div className="pzd-ds-label">At risk</div>
              <div className="pzd-ds-value pzd-ds-amount">{fmtMoney(denial.amount)}</div>
              <div className="pzd-ds-sub">{denial.carc} · {denial.rarc}</div>
            </div>
          </div>
          <div className="pzd-ds-ai">
            <div className="pzd-ds-ai-bar">
              <div className="pzd-ds-ai-fill" style={{ width: `${denial.winProbability * 100}%` }} />
            </div>
            <div className="pzd-ds-ai-label">
              PanelZero win-probability <strong>{Math.round(denial.winProbability * 100)}%</strong>
              <span className="pzd-ds-ai-sub">
                · based on {denial.payer} history, CARC {denial.carc}, and clinical match
              </span>
            </div>
            {p2p && (
              <button
                type="button"
                className="pzd-ds-p2pchip"
                onClick={() => { cancelAuto(); setTab("p2p"); }}
                title="Open P2P Review"
              >
                <span className="pzd-p2p-dot" />
                P2P recommended · <strong>+{p2pUplift} pts</strong> uplift
                <span className="pzd-ds-p2pchip-go">→</span>
              </button>
            )}
          </div>
        </div>

        <div className="pzd-tabs" role="tablist">
          {[
            ["letter",   "Appeal letter"],
            ["p2p",      "P2P Review"],
            ["package",  "Full appeal package"],
            ["imaging",  "Imaging"],
            ["eob",      "EOB / 835"],
          ].map(([id, label]) => (
            <button
              key={id}
              role="tab"
              aria-selected={tab === id}
              className={`pzd-tab ${tab === id ? "is-active" : ""}`}
              onClick={() => { cancelAuto(); setTab(id); }}
            >
              {label}
            </button>
          ))}
        </div>

        <div className={`pzd-drawer-body ${(tab === "p2p" || tab === "package") ? "pzd-drawer-body--no-side" : ""}`}>
         <div className="pzd-drawer-main">
          {tab === "letter" && (
            <div className="pzd-letter">
              <div className="pzd-letter-doc">
                <div className="pzd-letterhead">
                  <img src={logo} alt="" className="pzd-letterhead-logo" />
                  <div>
                    <div className="pzd-letterhead-name">Stanford Healthcare</div>
                    <div className="pzd-letterhead-sub">Revenue Cycle · 300 Pasteur Dr. · Stanford, CA</div>
                  </div>
                  <div className="pzd-letterhead-date">April 16, 2026</div>
                </div>
                <pre className="pzd-letter-body">{typed}<span className="pzd-letter-caret">▎</span></pre>
              </div>
              <div className="pzd-letter-foot">
                <div className="pzd-letter-meta">
                  <span className="pzd-chipsoft">GPT-5.5 · pz-apl-7F4C</span>
                  <span className="pzd-chipsoft">Evidence sha256:1a3d…c8</span>
                  <span className="pzd-chipsoft">Confidence {(denial.aiScore*100).toFixed(0)}%</span>
                </div>
              </div>
            </div>
          )}

          {tab === "p2p" && <P2PTab denial={denial} />}

          {tab === "package" && (
            <AppealPackagePDF
              denial={denial}
              onMaximize={() => setPdfMax((m) => !m)}
              maxed={pdfMax}
              page={pdfPage}
              setPage={setPdfPage}
              autoPlaying={autoPlaying}
              onUserInteract={cancelAuto}
            />
          )}

          {tab === "imaging" && <ImagingViewer denial={denial} />}

          {tab === "eob" && (
            <div className="pzd-eob">
              <pre>{EOB_SNIPPET}</pre>
              <p className="pzd-eob-note">
                Ingested as 835 from your clearinghouse 2026-04-15 at 06:02 UTC. PanelZero parsed
                the remit and matched the claim to encounter 2026-03-18 via Epic FHIR (CPT {denial.cpt}, Dr. L. Nakamura).
              </p>
            </div>
          )}
         </div>
         {tab !== "p2p" && tab !== "package" && (
           <DrawerSidePanel
             denial={denial}
             onOpenP2P={() => { cancelAuto(); setTab("p2p"); }}
             onOpenPackage={() => { cancelAuto(); setTab("package"); }}
             onOpenVoice={() => onNavigate?.("voice")}
             onOpenFax={() => onNavigate?.("faxes")}
           />
         )}
        </div>

        <footer className="pzd-drawer-foot">
          <button className="pzd-btn pzd-btn-ghost">Edit draft</button>
          <button className="pzd-btn pzd-btn-ghost">Attach more</button>
          <button
            className={`pzd-btn pzd-btn-primary ${sent ? "is-sent" : ""}`}
            onClick={() => setSent(true)}
            disabled={sent}
          >
            {sent ? (
              <>
                <span className="pzd-check">✓</span>
                Submitted to Anthem · case #A-48192
              </>
            ) : (
              <>
                Submit via Availity portal
                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                  <path d="M5 12h12M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </>
            )}
          </button>
        </footer>
      </aside>
    </>
  );
}

/* ──────────────────────────────────────────────────────────────
   "Full appeal package" multi-page PDF preview — patterned after the
   reference app's /api/appeals/[id]/package-preview output:
     Cover → Appeal Letter → Evidence Index → Exhibit A/B/C/D
   ────────────────────────────────────────────────────────────── */
const EXHIBITS = [
  {
    letter: "A",
    title:  "Clinical Justification · Operative Narrative",
    source: "PanelZero · structured clinical summary",
    type:   "FHIR: CarePlan/cp-8821",
    relevance: 0.96,
    body:   CLINICAL_NARRATIVE,
    kind:   "narrative",
  },
  {
    letter: "B",
    title:  "Diagnostic Imaging · Left Knee MRI + AP Radiograph",
    source: "Stanford Radiology · Acc RAD-00841-26",
    type:   "FHIR: DiagnosticReport/dr-8821",
    relevance: 0.94,
    kind:   "imaging",
  },
  {
    letter: "C",
    title:  "Operative Report · 2026-03-18",
    source: "Stanford Orthopedic Center · Dr. L. Nakamura",
    type:   "FHIR: Procedure/proc-8821",
    relevance: 0.92,
    body:   OP_NOTE_EXCERPT,
    kind:   "operative",
  },
  {
    letter: "D",
    title:  "Policy Citations · LCD L33410 + Anthem SURG.00087",
    source: "CMS / Anthem commercial medical policy",
    type:   "clinical_guideline",
    relevance: 0.90,
    body:   LCD_CITATION,
    kind:   "policy",
  },
];

function buildPackagePages() {
  return [
    { slug: "cover",    title: "Cover",                           kind: "cover" },
    { slug: "letter",   title: "Appeal Letter",                   kind: "letter" },
    { slug: "index",    title: "Evidence Index",                  kind: "index" },
    ...EXHIBITS.map((e) => ({
      slug: `ex-${e.letter}`,
      title: `Exhibit ${e.letter} · ${e.title}`,
      kind:  e.kind === "imaging" ? "imaging" : e.kind,
      exhibit: e,
    })),
  ];
}

function AppealPackagePDF({
  denial,
  onMaximize,
  maxed,
  page,
  setPage,
  autoPlaying,
  onUserInteract,
}) {
  const pages = useMemo(buildPackagePages, []);
  const totalPages = pages.length;
  const handleNav = (fn) => { onUserInteract?.(); fn(); };
  return (
    <div className={`pzd-pdf ${maxed ? "is-max" : ""}`}>
      <div className="pzd-pdf-toolbar">
        <div className="pzd-pdf-title">
          appeal_package_{denial.claimId}.pdf
          <span className="pzd-pdf-sub">· {totalPages} pages · 1.2 MB</span>
        </div>
        <div className="pzd-pdf-ctrls">
          {autoPlaying && (
            <span className="pzd-pdf-auto">
              <span className="pzd-pdf-auto-dot" />
              Auto-preview · page {page + 1} of {totalPages}
              <em>· click any page to pause</em>
            </span>
          )}
          <button onClick={() => handleNav(() => setPage(Math.max(0, page - 1)))} disabled={page === 0} aria-label="Previous page">‹</button>
          <span className="pzd-pdf-count">{page + 1} / {totalPages}</span>
          <button onClick={() => handleNav(() => setPage(Math.min(totalPages - 1, page + 1)))} disabled={page === totalPages - 1} aria-label="Next page">›</button>
          <button className="pzd-pdf-max" onClick={() => handleNav(onMaximize)} aria-label="Maximize">
            {maxed ? "Restore" : "Expand"}
          </button>
          <button className="pzd-pdf-dl" onClick={() => window.print()} title="Print / save as PDF">Download</button>
        </div>
      </div>

      <div className="pzd-pdf-stage2">
        <aside className="pzd-pdf-rail" aria-label="Pages">
          {pages.map((p, idx) => (
            <button
              key={p.slug}
              className={`pzd-pdf-rail-item ${idx === page ? "is-active" : ""} ${idx < page ? "is-done" : ""}`}
              onClick={() => handleNav(() => setPage(idx))}
              title={p.title}
            >
              <span className="pzd-pdf-rail-thumb">
                <span className="pzd-pdf-rail-kind" aria-hidden="true">{pageKindGlyph(p.kind)}</span>
                <span className="pzd-pdf-rail-num">{idx + 1}</span>
              </span>
              <span className="pzd-pdf-rail-title">{p.title}</span>
            </button>
          ))}
        </aside>

        <div className="pzd-pdf-viewport">
          {pages.map((p, idx) => (
            <article
              className={`pzd-pdf-page ${idx === page ? "is-active" : ""}`}
              key={p.slug}
              aria-hidden={idx !== page}
              onClick={() => onUserInteract?.()}
            >
              {p.kind === "cover"
                ? <PDFPageCover denial={denial} totalPages={totalPages} exhibits={EXHIBITS.length} />
                : p.kind === "letter"
                  ? <PDFPageLetter denial={denial} pageNum={idx + 1} totalPages={totalPages} />
                  : p.kind === "index"
                    ? <PDFPageIndex denial={denial} pageNum={idx + 1} totalPages={totalPages} />
                    : <PDFPageExhibit denial={denial} page={p} pageNum={idx + 1} totalPages={totalPages} />}
            </article>
          ))}
          {autoPlaying && (
            <div className="pzd-pdf-autoprogress" aria-hidden="true">
              <div className="pzd-pdf-autoprogress-bar" style={{ width: `${((page + 1) / totalPages) * 100}%` }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function pageKindGlyph(kind) {
  switch (kind) {
    case "cover":     return "★";
    case "letter":    return "✉";
    case "index":     return "≡";
    case "imaging":   return "◪";
    case "narrative": return "¶";
    case "operative": return "⧉";
    case "policy":    return "§";
    default:          return "•";
  }
}

function PDFWatermark() {
  return <div className="pzd-pdf-wm">DEMO PACKAGE · NOT A REAL PDF</div>;
}

function PDFPageNum({ n, total }) {
  return <div className="pzd-pdf-pagenum">Page {n} of {total}</div>;
}

function PDFPageCover({ denial, totalPages, exhibits }) {
  const prepared = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  return (
    <div className="pzd-pdf-cover">
      <PDFWatermark />
      <h1>Insurance Appeal Package</h1>
      <div className="pzd-pdf-cover-sub">PanelZero · Prepared for submission to insurer</div>

      <div className="pzd-pdf-meta">
        <div className="pzd-pdf-meta-k">Appeal ID</div>
        <div className="pzd-pdf-meta-v">apl_7F4C-{denial.claimId.slice(-6)}</div>
        <div className="pzd-pdf-meta-k">Appeal Level</div>
        <div className="pzd-pdf-meta-v">Level 1 · Standard Reconsideration</div>
        <div className="pzd-pdf-meta-k">Version</div>
        <div className="pzd-pdf-meta-v">v3 · auto-draft</div>
        <div className="pzd-pdf-meta-k">Review Status</div>
        <div className="pzd-pdf-meta-v">Approved for submission</div>
        <div className="pzd-pdf-meta-k">Addressed to</div>
        <div className="pzd-pdf-meta-v">{denial.payer} · Appeals Department</div>
        <div className="pzd-pdf-meta-k">Patient / Claim</div>
        <div className="pzd-pdf-meta-v">{denial.patient.name} · MRN {denial.patient.mrn} · {denial.claimId}</div>
        <div className="pzd-pdf-meta-k">Service</div>
        <div className="pzd-pdf-meta-v">CPT {denial.cpt} · {denial.service}</div>
        <div className="pzd-pdf-meta-k">Amount at risk</div>
        <div className="pzd-pdf-meta-v pzd-pdf-meta-v--amount">{fmtMoney(denial.amount)}</div>
        <div className="pzd-pdf-meta-k">Prepared</div>
        <div className="pzd-pdf-meta-v">{prepared}</div>
        <div className="pzd-pdf-meta-k">Evidence Exhibits</div>
        <div className="pzd-pdf-meta-v">{exhibits} (Exhibits A–{String.fromCharCode(64 + exhibits)})</div>
        <div className="pzd-pdf-meta-k">Total Pages</div>
        <div className="pzd-pdf-meta-v">{totalPages}</div>
      </div>

      <div className="pzd-pdf-stamp">PanelZero · Ready to Fax</div>
      <PDFPageNum n={1} total={totalPages} />
    </div>
  );
}

function PDFPageLetter({ denial, pageNum, totalPages }) {
  const paragraphs = APPEAL_LETTER.split("\n\n");
  return (
    <div className="pzd-pdf-letterpg">
      <PDFWatermark />
      <div className="pzd-pdf-section-title">Appeal Letter</div>
      <div className="pzd-pdf-letter">
        {paragraphs.map((p, i) => (
          <p key={i}>{p.split("\n").map((line, j, arr) => (
            <React.Fragment key={j}>{line}{j < arr.length - 1 ? <br /> : null}</React.Fragment>
          ))}</p>
        ))}
      </div>
      <PDFPageNum n={pageNum} total={totalPages} />
    </div>
  );
}

function PDFPageIndex({ denial, pageNum, totalPages }) {
  return (
    <div className="pzd-pdf-indexpg">
      <PDFWatermark />
      <div className="pzd-pdf-section-title">Evidence Index ({EXHIBITS.length} items)</div>
      <table className="pzd-pdf-evtable">
        <thead>
          <tr>
            <th style={{ width: 30 }}>#</th>
            <th>Source</th>
            <th style={{ width: 140 }}>Type</th>
            <th style={{ width: 70 }}>Exhibit</th>
            <th style={{ width: 70 }}>Relevance</th>
          </tr>
        </thead>
        <tbody>
          {EXHIBITS.map((e, i) => (
            <tr key={e.letter}>
              <td className="num">{i + 1}</td>
              <td>
                <div className="ev-name">{e.title}</div>
                <div className="ev-meta">{e.source}</div>
              </td>
              <td>{e.type}</td>
              <td className="num">Ex. {e.letter}</td>
              <td className="num">{Math.round(e.relevance * 100)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="pzd-pdf-indexnote">
        Each exhibit is included in full on the following pages. FHIR identifiers reference
        resources on the {denial.patient.name} record (MRN {denial.patient.mrn}).
      </p>
      <PDFPageNum n={pageNum} total={totalPages} />
    </div>
  );
}

function PDFPageExhibit({ denial, page, pageNum, totalPages }) {
  const e = page.exhibit;
  return (
    <div className="pzd-pdf-expg">
      <PDFWatermark />
      <div className="pzd-pdf-exbanner">
        <div>
          <div className="lbl">EXHIBIT {e.letter}</div>
          <div className="ttl">{e.title}</div>
        </div>
        <div className="rt">
          <div>{e.type}</div>
          <div>relevance {Math.round(e.relevance * 100)}%</div>
        </div>
      </div>

      {page.kind === "imaging"
        ? <ImagingExhibit denial={denial} />
        : <pre className="pzd-pdf-extext">{e.body}</pre>}

      <PDFPageNum n={pageNum} total={totalPages} />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   Simple icon set
   ────────────────────────────────────────────────────────────── */
function IconQueue() {
  return (<svg viewBox="0 0 24 24" width="18" height="18"><path d="M4 6h16M4 12h12M4 18h8" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" fill="none"/></svg>);
}
function IconMail() {
  return (<svg viewBox="0 0 24 24" width="18" height="18"><path d="M3 6h18v12H3z M3 6l9 7 9-7" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" fill="none"/></svg>);
}
function IconChart() {
  return (<svg viewBox="0 0 24 24" width="18" height="18"><path d="M4 20V8m5 12v-6m5 6V4m5 16v-9" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" fill="none"/></svg>);
}
function IconPlug() {
  return (<svg viewBox="0 0 24 24" width="18" height="18"><path d="M9 4v5M15 4v5M7 9h10v3a5 5 0 0 1-10 0V9zM12 17v4" stroke="currentColor" strokeWidth="1.7" fill="none" strokeLinecap="round"/></svg>);
}
function IconGear() {
  return (<svg viewBox="0 0 24 24" width="18" height="18"><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7" fill="none"/><path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4L7 17M17 7l1.4-1.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>);
}
function IconPhone() {
  return (<svg viewBox="0 0 24 24" width="18" height="18"><path d="M5 4h3l2 5-2 1a12 12 0 0 0 6 6l1-2 5 2v3a2 2 0 0 1-2 2A17 17 0 0 1 3 6a2 2 0 0 1 2-2z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>);
}
function IconPrinter() {
  return (<svg viewBox="0 0 24 24" width="18" height="18"><path d="M6 9V3h12v6M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v7H6z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"/></svg>);
}

/* ──────────────────────────────────────────────────────────────
   Analytics view (simple but real-looking)
   ────────────────────────────────────────────────────────────── */
function AnalyticsView() {
  return (
    <section className="pzd-main pzd-analytics">
      <header className="pzd-main-head">
        <div>
          <h1 className="pzd-h1">Analytics</h1>
          <p className="pzd-h1-sub">Denial recovery, turnaround, and payer-level trends.</p>
        </div>
      </header>
      <StatStrip />

      <div className="pzd-charts">
        <div className="pzd-chart pzd-chart-wide">
          <div className="pzd-chart-title">Denied vs. recovered · last 12 weeks</div>
          <FakeBarChart />
        </div>
        <div className="pzd-chart">
          <div className="pzd-chart-title">Overturn by payer</div>
          <div className="pzd-bars">
            {[
              ["Anthem BCBS", 72, "#14b8b8"],
              ["UnitedHealthcare", 58, "#18d1d1"],
              ["Aetna", 66, "#0b6a6a"],
              ["Cigna", 49, "#2563eb"],
              ["Humana", 54, "#7c3aed"],
              ["Kaiser", 61, "#10b981"],
            ].map(([name, pct, color]) => (
              <div className="pzd-bar-row" key={name}>
                <span className="pzd-bar-label">{name}</span>
                <span className="pzd-bar-track">
                  <span className="pzd-bar-fill" style={{ width: `${pct}%`, background: color }} />
                </span>
                <span className="pzd-bar-pct">{pct}%</span>
              </div>
            ))}
          </div>
        </div>
        <div className="pzd-chart">
          <div className="pzd-chart-title">Top CARC codes · this month</div>
          <div className="pzd-carcs">
            {[
              ["CO-197", 31, "Prior auth missing"],
              ["CO-50",  22, "Not medically necessary"],
              ["CO-16",  19, "Claim lacks info"],
              ["N115",   14, "LCD / policy"],
              ["PR-204",  9, "Non-covered"],
            ].map(([code, pct, label]) => (
              <div className="pzd-carc-row" key={code}>
                <span className="pzd-carc-code">{code}</span>
                <span className="pzd-carc-label">{label}</span>
                <span className="pzd-carc-pct">{pct}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function FakeBarChart() {
  // 12 bars of denied (taller) and recovered (shorter) overlaid
  const weeks = Array.from({ length: 12 }, (_, i) => {
    const denied = 40 + Math.round(Math.sin(i * 0.9) * 12 + Math.random() * 10);
    const recovered = Math.round(denied * (0.5 + Math.random() * 0.25));
    return { denied, recovered, label: `W${52 - 11 + i}` };
  });
  const max = Math.max(...weeks.map((w) => w.denied));
  return (
    <div className="pzd-barchart">
      {weeks.map((w, i) => (
        <div className="pzd-barchart-col" key={i} style={{ "--pzd-i": i }}>
          <div className="pzd-barchart-stack">
            <div className="pzd-barchart-denied" style={{ height: `${(w.denied / max) * 100}%` }} />
            <div className="pzd-barchart-recovered" style={{ height: `${(w.recovered / max) * 100}%` }} />
          </div>
          <div className="pzd-barchart-label">{w.label}</div>
        </div>
      ))}
      <div className="pzd-barchart-legend">
        <span className="pzd-legend-item"><span className="pzd-legend-dot" style={{background:"#d8e2f0"}} /> Denied</span>
        <span className="pzd-legend-item"><span className="pzd-legend-dot" style={{background:"#14b8b8"}} /> Recovered</span>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   Appeals list (simple table of drafts)
   ────────────────────────────────────────────────────────────── */
function AppealsView({ onSelect }) {
  const drafted = DENIALS.filter((d) => d.status !== "queued" && d.status !== "closed");
  return (
    <section className="pzd-main">
      <header className="pzd-main-head">
        <div>
          <h1 className="pzd-h1">Appeals · drafted &amp; in-flight</h1>
          <p className="pzd-h1-sub">Each item has a ready-to-send PDF package. Click to review.</p>
        </div>
      </header>
      <div className="pzd-tablehead">
        <div>Claim</div><div>Patient</div><div>Payer</div><div>Service</div>
        <div>Denial reason</div><div>Win %</div><div style={{textAlign:"right"}}>At risk</div><div>Status</div>
      </div>
      <div className="pzd-rows">
        {drafted.map((d, i) => (
          <DenialRow d={d} key={d.id} delay={i * 50} active={false} onClick={() => onSelect(d)} />
        ))}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────
   Drawer side panel — persistent P2P + voice + fax status strip
   rendered to the right of tab content so it's always visible.
   ────────────────────────────────────────────────────────────── */
function DrawerSidePanel({ denial, onOpenP2P, onOpenVoice, onOpenFax, onOpenPackage }) {
  const p = P2P[denial.id];
  const uplift = p ? Math.round((p.winWith - p.winWithout) * 100) : 0;
  const lastVoice = VOICE_CALLS.find((c) => c.claimId === denial.claimId);
  const lastFax   = FAXES.find((f) => f.denialId === denial.id);
  const vMeta = lastVoice ? VOICE_STATUS_META[lastVoice.status] : null;
  const fMeta = lastFax   ? FAX_STATUS_META[lastFax.status]     : null;

  return (
    <aside className="pzd-side2">
      <div className="pzd-side2-head">
        <div className="pzd-side2-head-kicker">Case companion</div>
        <div className="pzd-side2-head-claim">{denial.claimId}</div>
      </div>

      {p && (
        <div className="pzd-side2-card pzd-side2-p2p">
          <div className="pzd-side2-card-row">
            <span className="pzd-p2p-dot" />
            <span className="pzd-side2-card-title">P2P recommended</span>
          </div>
          <div className="pzd-side2-p2p-num">
            +{uplift} <small>pts uplift</small>
          </div>
          <div className="pzd-side2-p2p-sub">
            {Math.round(p.winWithout * 100)}% <span className="pzd-side2-arrow">→</span> {Math.round(p.winWith * 100)}%
          </div>
          <div className="pzd-side2-kv">
            <div><span>MD</span>{p.medicalDirector.split(",")[0]}</div>
            <div><span>Slot</span>{p.nextAvailable}</div>
          </div>
          <button className="pzd-side2-btn pzd-side2-btn-primary" onClick={onOpenP2P}>Open P2P Review →</button>
        </div>
      )}

      <div className="pzd-side2-card">
        <div className="pzd-side2-card-row">
          <span className="pzd-side2-ico">📞</span>
          <span className="pzd-side2-card-title">Voice agent</span>
          {vMeta && <span className={`pzd-intstatus pzd-intstatus--${vMeta.tone}`}><span className="pzd-intstatus-dot" />{vMeta.label}</span>}
        </div>
        {lastVoice ? (
          <>
            <div className="pzd-side2-card-body">{lastVoice.purpose}</div>
            <div className="pzd-side2-card-sub">{fmtPhone(lastVoice.phone)} · {lastVoice.startedAt || "queued"}</div>
            {lastVoice.referenceNumber && <div className="pzd-side2-card-sub">Ref · {lastVoice.referenceNumber}</div>}
          </>
        ) : (
          <div className="pzd-side2-card-body pzd-side2-card-muted">No calls logged for this claim yet.</div>
        )}
        <button className="pzd-side2-btn" onClick={onOpenVoice}>Open Voice tab →</button>
      </div>

      <div className="pzd-side2-card">
        <div className="pzd-side2-card-row">
          <span className="pzd-side2-ico">📠</span>
          <span className="pzd-side2-card-title">Fax</span>
          {fMeta && <span className={`pzd-intstatus pzd-intstatus--${fMeta.tone}`}><span className="pzd-intstatus-dot" />{fMeta.label}</span>}
        </div>
        {lastFax ? (
          <>
            <div className="pzd-side2-card-body">{lastFax.toLabel}</div>
            <div className="pzd-side2-card-sub">
              {lastFax.pages} pages · attempts {lastFax.attempts}/{lastFax.maxAttempts}
            </div>
            {lastFax.confirmation && <div className="pzd-side2-card-sub">Conf · {lastFax.confirmation}</div>}
            {lastFax.lastError && <div className="pzd-side2-card-err">{lastFax.lastError}</div>}
          </>
        ) : (
          <div className="pzd-side2-card-body pzd-side2-card-muted">No fax transmissions yet.</div>
        )}
        <button className="pzd-side2-btn" onClick={onOpenFax}>Open Faxes tab →</button>
      </div>

      <div className="pzd-side2-card pzd-side2-actions">
        <div className="pzd-side2-card-title">Quick actions</div>
        <button className="pzd-side2-btn" onClick={onOpenPackage}>Full appeal package</button>
        <button className="pzd-side2-btn pzd-side2-btn-ghost">Regenerate draft with GPT-5.5</button>
        <button className="pzd-side2-btn pzd-side2-btn-ghost">Forward to physician review</button>
      </div>
    </aside>
  );
}

/* ──────────────────────────────────────────────────────────────
   Peer-to-peer review — rendered inside its own tab so it never
   blocks the appeal package.
   ────────────────────────────────────────────────────────────── */
function P2PTab({ denial }) {
  const p = P2P[denial.id];
  const [scheduled, setScheduled] = useState(false);
  const without = useCountUp(p ? p.winWithout * 100 : 0, { duration: 1000 });
  const withP   = useCountUp(p ? p.winWith    * 100 : 0, { duration: 1400 });
  if (!p) {
    return (
      <div className="pzd-p2p-empty">
        <div className="pzd-placeholder-dot" />
        <span>No peer-to-peer review recommended for this denial.</span>
      </div>
    );
  }
  const uplift = Math.round((p.winWith - p.winWithout) * 100);
  return (
    <div className="pzd-p2ptab">
      <div className="pzd-p2ptab-banner">
        <div className="pzd-p2ptab-banner-left">
          <div className="pzd-p2ptab-banner-kicker">
            <span className="pzd-p2p-dot" />
            Peer-to-Peer Review · <strong>Recommended</strong>
          </div>
          <div className="pzd-p2ptab-banner-title">
            PanelZero predicts a <strong>+{uplift} pt</strong> win-rate uplift if a P2P is completed.
          </div>
          <div className="pzd-p2ptab-banner-sub">
            Based on {denial.payer}&apos;s behavior on {denial.carc} denials and the clinical strength of this case.
          </div>
        </div>
        <div className="pzd-p2ptab-uplift">
          <div className="pzd-p2p-metric">
            <div className="pzd-p2p-metric-label">Without P2P</div>
            <div className="pzd-p2p-metric-value pzd-p2p-metric-value--low">{without.toFixed(0)}%</div>
          </div>
          <div className="pzd-p2p-arrow" aria-hidden="true">
            <svg viewBox="0 0 40 16" width="36" height="14">
              <path d="M2 8h32M28 3l6 5-6 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="pzd-p2p-metric">
            <div className="pzd-p2p-metric-label">With P2P</div>
            <div className="pzd-p2p-metric-value pzd-p2p-metric-value--high">{withP.toFixed(0)}%</div>
          </div>
          <div className="pzd-p2p-delta">+{uplift} pts</div>
        </div>
      </div>

      <div className="pzd-p2ptab-grid">
        <div className="pzd-p2ptab-card">
          <div className="pzd-p2ptab-card-label">Payer medical director</div>
          <div className="pzd-p2ptab-card-value">{p.medicalDirector}</div>
          <div className="pzd-p2ptab-card-sub">Next open slot · {p.nextAvailable} · {p.durationMin} min</div>
          <button
            className={`pzd-btn pzd-btn-primary pzd-p2ptab-cta ${scheduled ? "is-sent" : ""}`}
            onClick={() => setScheduled(true)}
            disabled={scheduled}
          >
            {scheduled
              ? (<><span className="pzd-check">✓</span>P2P booked · {p.nextAvailable}</>)
              : "Schedule P2P via Voice Agent"}
          </button>
        </div>

        <div className="pzd-p2ptab-card pzd-p2ptab-points">
          <div className="pzd-p2ptab-card-label">Talking points</div>
          <ol>{p.talkingPoints.map((t, i) => <li key={i}>{t}</li>)}</ol>
        </div>

        <div className="pzd-p2ptab-card">
          <div className="pzd-p2ptab-card-label">Evidence bookmarks</div>
          <div className="pzd-p2ptab-card-sub" style={{ marginBottom: 10 }}>Pages in the appeal package to open during the call.</div>
          <div className="pzd-p2ptab-chips">
            {p.evidence.map((e) => (
              <span className="pzd-p2p-chip" key={e.label}>{e.label} · p{e.pageRef}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   Imaging viewer (real Wikimedia Commons images + DICOM-style overlays)
   ────────────────────────────────────────────────────────────── */
function DicomImage({ series, overlays, className = "" }) {
  return (
    <div className={`pzd-dicom ${className}`}>
      <img src={series.image} alt={`${series.view} (demo)`} loading="lazy" />
      {overlays}
      <div className="pzd-dicom-orient pzd-dicom-orient--l">{series.orientationL}</div>
      <div className="pzd-dicom-orient pzd-dicom-orient--r">{series.orientationR}</div>
    </div>
  );
}

function ImagingViewer({ denial }) {
  const study = IMAGING[denial.id];
  const [active, setActive] = useState(0);
  if (!study) {
    return (
      <div className="pzd-img-empty">
        <div className="pzd-placeholder-dot" />
        <span>No imaging linked to this denial.</span>
      </div>
    );
  }
  const current = study.series[active];

  return (
    <div className="pzd-img">
      <div className="pzd-img-chrome">
        <div className="pzd-img-modality">
          <span className="pzd-img-modality-tag">{study.modality}</span>
          <span className="pzd-img-body">{study.bodyPart}</span>
        </div>
        <div className="pzd-img-meta">
          <span>Accession {study.accession}</span>
          <span>·</span>
          <span>Study {study.studyDate}</span>
          <span>·</span>
          <span>Read by {study.radiologist}</span>
        </div>
      </div>

      <div className="pzd-img-stage">
        <div className="pzd-img-viewer">
          <DicomImage
            series={current}
            overlays={
              <>
                <div className="pzd-dicom-overlay pzd-dicom-overlay--tl">
                  <div>{study.facility}</div>
                  <div className="pzd-dicom-sub">{study.department}</div>
                  <div className="pzd-dicom-sub">Acc {study.accession}</div>
                </div>
                <div className="pzd-dicom-overlay pzd-dicom-overlay--tr">
                  <div>{study.modality} · {current.modalityTag}</div>
                  <div className="pzd-dicom-sub">{current.view}</div>
                  <div className="pzd-dicom-sub">Slice {current.slice}</div>
                </div>
                <div className="pzd-dicom-overlay pzd-dicom-overlay--bl">
                  <div>{denial.patient.name} · MRN {denial.patient.mrn}</div>
                  <div className="pzd-dicom-sub">DOB {denial.patient.dob} · {denial.patient.sex}</div>
                  <div className="pzd-dicom-sub">Study {study.studyDate}</div>
                </div>
                <div className="pzd-dicom-overlay pzd-dicom-overlay--br">
                  <div>W/L 1200 · 600</div>
                  <div className="pzd-dicom-sub">FOV 14cm</div>
                  <div className="pzd-dicom-sub">PanelZero viewer</div>
                </div>
              </>
            }
          />
          <div className="pzd-img-tools" aria-label="Viewer tools">
            {["W/L","Pan","Zoom","Measure","Scroll"].map((t) => (
              <button key={t} className="pzd-img-tool">{t}</button>
            ))}
          </div>
        </div>

        <aside className="pzd-img-side">
          <div className="pzd-img-side-title">Series ({study.series.length})</div>
          <div className="pzd-img-thumbs">
            {study.series.map((s, i) => (
              <button
                key={s.id}
                className={`pzd-img-thumb ${i === active ? "is-active" : ""}`}
                onClick={() => setActive(i)}
              >
                <div className="pzd-img-thumb-canvas">
                  <img src={s.image} alt="" loading="lazy" />
                </div>
                <div className="pzd-img-thumb-label">
                  <div>{s.view}</div>
                  <div className="pzd-img-thumb-sub">Slice {s.slice}</div>
                </div>
              </button>
            ))}
          </div>

          <div className="pzd-img-report">
            <div className="pzd-img-side-title">Radiologist impression</div>
            <p>{study.impression}</p>
            <div className="pzd-img-finding">
              <span className="pzd-img-finding-dot" />
              {study.finding}
            </div>
          </div>

          <div className="pzd-img-attrib">
            {study.series.map((s) => (
              <div key={s.id}>
                <strong>{s.view}:</strong>{" "}
                <a href={s.creditUrl} target="_blank" rel="noreferrer">{s.credit}</a>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}

/* Compact imaging render used inside a PDF exhibit page */
function ImagingExhibit({ denial }) {
  const study = IMAGING[denial.id];
  if (!study) return null;
  return (
    <div className="pzd-pdf-img2">
      <div className="pzd-pdf-doc-head">
        <div>
          <div className="pzd-pdf-org">{study.facility.toUpperCase()}</div>
          <div className="pzd-pdf-dept">{study.department}</div>
        </div>
        <div className="pzd-pdf-doc-right">
          <div className="pzd-pdf-big">DIAGNOSTIC IMAGING REPORT</div>
          <div className="pzd-pdf-mono">Accession · {study.accession}</div>
        </div>
      </div>

      <div className="pzd-pdf-pt-strip">
        <div><span className="pzd-pdf-k">MRN</span>{denial.patient.mrn}</div>
        <div><span className="pzd-pdf-k">DOB</span>{denial.patient.dob}</div>
        <div><span className="pzd-pdf-k">Sex</span>{denial.patient.sex}</div>
        <div><span className="pzd-pdf-k">Exam</span>{study.modality} {study.bodyPart}</div>
        <div><span className="pzd-pdf-k">Date</span>{study.studyDate}</div>
        <div><span className="pzd-pdf-k">Read by</span>{study.radiologist}</div>
      </div>

      <div className="pzd-pdf-img-grid">
        {study.series.slice(0, 2).map((s) => (
          <figure className="pzd-pdf-fig" key={s.id}>
            <DicomImage
              series={s}
              className="is-compact"
              overlays={
                <>
                  <div className="pzd-dicom-overlay pzd-dicom-overlay--tl"><div>{study.modality} · {s.modalityTag}</div><div className="pzd-dicom-sub">{s.view}</div></div>
                  <div className="pzd-dicom-overlay pzd-dicom-overlay--br"><div className="pzd-dicom-sub">Slice {s.slice}</div></div>
                </>
              }
            />
            <figcaption>
              <strong>{s.view}</strong>
              <span className="pzd-pdf-fig-credit">{s.credit}</span>
            </figcaption>
          </figure>
        ))}
      </div>

      <div className="pzd-pdf-doc-section">
        <h4>Clinical Indication</h4>
        <p>{study.indication}</p>
      </div>
      <div className="pzd-pdf-doc-section">
        <h4>Technique</h4>
        <p>{study.technique}</p>
      </div>
      <div className="pzd-pdf-doc-section">
        <h4>Comparison</h4>
        <p>{study.comparison}</p>
      </div>
      <div className="pzd-pdf-doc-section">
        <h4>Findings</h4>
        <ul>{study.findings.map((f, i) => <li key={i}>{f}</li>)}</ul>
      </div>
      <div className="pzd-pdf-callout-red">
        <strong>Impression: </strong>{study.impression}
      </div>
      <div className="pzd-pdf-sig">
        <div>Electronically signed</div>
        <div className="pzd-pdf-sig-name">{study.radiologist}</div>
        <div>{study.radiologistTitle}</div>
      </div>
    </div>
  );
}


/* ──────────────────────────────────────────────────────────────
   Faxes view (SRFax-style)
   ────────────────────────────────────────────────────────────── */
function fmtPhone(p) {
  if (!p) return "—";
  const d = p.replace(/[^0-9]/g, "");
  if (d.length === 11 && d.startsWith("1")) return `+1 (${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  return p;
}

function fmtDuration(s) {
  if (!s || s < 1) return "—";
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
}

function FaxRow({ f, expanded, onToggle }) {
  const meta = FAX_STATUS_META[f.status] || FAX_STATUS_META.queued;
  return (
    <>
      <tr className={`pzd-vrow ${expanded ? "is-open" : ""}`} onClick={onToggle}>
        <td className="pzd-v-id">{f.id.slice(3, 11)}</td>
        <td className="pzd-v-dir">
          <span className={`pzd-vdir pzd-vdir--${f.direction}`}>{f.direction}</span>
        </td>
        <td>
          <div className="pzd-v-who">{f.toLabel}</div>
          <div className="pzd-v-mono">{fmtPhone(f.to)}</div>
        </td>
        <td><span className={`pzd-intstatus pzd-intstatus--${meta.tone}`}><span className="pzd-intstatus-dot" />{meta.label}</span></td>
        <td className="pzd-v-num">{f.pages}</td>
        <td className="pzd-v-num">{f.attempts}/{f.maxAttempts}</td>
        <td className="pzd-v-mono">{f.sentAt || "—"}</td>
        <td className="pzd-v-mono">{f.deliveredAt || "—"}</td>
        <td className="pzd-v-actions">
          {f.status === "failed" && (
            <button className="pzd-v-retry" onClick={(e) => { e.stopPropagation(); }}>Retry</button>
          )}
          <span className={`pzd-v-caret ${expanded ? "is-open" : ""}`}>▸</span>
        </td>
      </tr>
      {expanded && (
        <tr className="pzd-vdetail-row">
          <td colSpan={9}>
            <div className="pzd-vdetail">
              <div className="pzd-vdetail-left">
                <h4>Fax Detail</h4>
                <dl className="pzd-vdl">
                  <div><dt>Status</dt><dd><span className={`pzd-intstatus pzd-intstatus--${meta.tone}`}><span className="pzd-intstatus-dot" />{meta.label}</span></dd></div>
                  <div><dt>To</dt><dd>{f.toLabel} · <code>{fmtPhone(f.to)}</code></dd></div>
                  <div><dt>From</dt><dd><code>{fmtPhone(f.from)}</code></dd></div>
                  <div><dt>Pages</dt><dd>{f.pages}</dd></div>
                  <div><dt>Attempts</dt><dd>{f.attempts}/{f.maxAttempts}</dd></div>
                  {f.confirmation && <div><dt>Confirmation</dt><dd><code>{f.confirmation}</code></dd></div>}
                  {f.sentAt && <div><dt>Sent</dt><dd>{f.sentAt}</dd></div>}
                  {f.deliveredAt && <div><dt>Delivered</dt><dd>{f.deliveredAt}</dd></div>}
                  {f.appealId && <div><dt>Appeal</dt><dd><code>{f.appealId}</code></dd></div>}
                  {f.denialId && <div><dt>Denial</dt><dd><code>{f.denialId}</code></dd></div>}
                  <div><dt>Document</dt><dd><code>{f.documentName}</code></dd></div>
                  {f.lastError && (
                    <div className="pzd-vdl-err">
                      <dt>Last error</dt>
                      <dd>{f.lastError}</dd>
                    </div>
                  )}
                </dl>
              </div>
              <div className="pzd-vdetail-right">
                <div className="pzd-vdetail-toolbar">
                  <span>Document preview</span>
                  <button className="pzd-v-openbtn" onClick={(e) => e.stopPropagation()}>Open in new tab ↗</button>
                </div>
                <div className="pzd-vdoc">
                  <div className="pzd-vdoc-paper">
                    <div className="pzd-vdoc-stamp">PanelZero · Ready to Fax</div>
                    <div className="pzd-vdoc-title">Insurance Appeal Package</div>
                    <div className="pzd-vdoc-sub">Re: Claim {f.denialId ? DENIALS.find(d => d.id === f.denialId)?.claimId : f.appealId}</div>
                    <div className="pzd-vdoc-meta">
                      <span>{f.documentName}</span>
                      <span>{f.pages} pages · rendered server-side by Puppeteer</span>
                    </div>
                    <div className="pzd-vdoc-lines">
                      {Array.from({ length: 10 }).map((_, i) => <div key={i} style={{ width: `${80 + Math.round(Math.sin(i)*15)}%` }} />)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function FaxesView() {
  const [expanded, setExpanded] = useState(null);
  return (
    <section className="pzd-main">
      <header className="pzd-main-head">
        <div>
          <h1 className="pzd-h1">Faxes</h1>
          <p className="pzd-h1-sub">Outbound + inbound fax transmissions via SRFax. Click a row to preview the document.</p>
        </div>
      </header>

      <div className="pzd-vbar">
        <span className="pzd-vbar-stat"><strong>{FAXES.filter(f => f.status === "delivered").length}</strong> delivered</span>
        <span className="pzd-vbar-stat"><strong>{FAXES.filter(f => ["sending","queued","sent"].includes(f.status)).length}</strong> in flight</span>
        <span className="pzd-vbar-stat"><strong>{FAXES.filter(f => f.status === "failed").length}</strong> failed · retry queued</span>
        <span className="pzd-vbar-stat"><strong>96%</strong> DLR success (30d)</span>
      </div>

      <div className="pzd-vtable-wrap">
        <table className="pzd-vtable">
          <thead>
            <tr>
              <th style={{ width: 90 }}>ID</th>
              <th style={{ width: 90 }}>Direction</th>
              <th>To / Destination</th>
              <th style={{ width: 120 }}>Status</th>
              <th style={{ width: 60, textAlign: "right" }}>Pages</th>
              <th style={{ width: 80, textAlign: "right" }}>Attempts</th>
              <th style={{ width: 150 }}>Sent</th>
              <th style={{ width: 150 }}>Delivered</th>
              <th style={{ width: 100 }}></th>
            </tr>
          </thead>
          <tbody>
            {FAXES.map((f) => (
              <FaxRow key={f.id} f={f} expanded={expanded === f.id} onToggle={() => setExpanded(expanded === f.id ? null : f.id)} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────
   Voice calls view (Vapi-style)
   ────────────────────────────────────────────────────────────── */
function VoiceRow({ c, expanded, onToggle }) {
  const meta = VOICE_STATUS_META[c.status] || VOICE_STATUS_META.queued;
  return (
    <>
      <tr className={`pzd-vrow ${expanded ? "is-open" : ""}`} onClick={onToggle}>
        <td className="pzd-v-id">{c.id.slice(3, 11)}</td>
        <td>
          <div className="pzd-v-who">{c.payer}</div>
          <div className="pzd-v-mono">{c.claimId || "—"}</div>
        </td>
        <td className="pzd-v-mono">{fmtPhone(c.phone)}</td>
        <td>
          <div>{c.purpose}</div>
        </td>
        <td><span className={`pzd-intstatus pzd-intstatus--${meta.tone}`}><span className="pzd-intstatus-dot" />{meta.label}</span></td>
        <td className="pzd-v-num">{fmtDuration(c.durationSeconds)}</td>
        <td>
          {c.disclosedAi
            ? <span className="pzd-v-ai pzd-v-ai--yes">Yes</span>
            : <span className="pzd-v-ai pzd-v-ai--no">No</span>}
        </td>
        <td className="pzd-v-mono">{c.startedAt || "—"}</td>
        <td className="pzd-v-actions">
          <span className={`pzd-v-caret ${expanded ? "is-open" : ""}`}>▸</span>
        </td>
      </tr>
      {expanded && (
        <tr className="pzd-vdetail-row">
          <td colSpan={9}>
            <div className="pzd-vdetail">
              <div className="pzd-vdetail-left">
                <h4>Call Detail</h4>
                <dl className="pzd-vdl">
                  <div><dt>Status</dt><dd><span className={`pzd-intstatus pzd-intstatus--${meta.tone}`}><span className="pzd-intstatus-dot" />{meta.label}</span></dd></div>
                  <div><dt>Payer</dt><dd>{c.payer}</dd></div>
                  <div><dt>Phone</dt><dd><code>{fmtPhone(c.phone)}</code></dd></div>
                  <div><dt>Purpose</dt><dd>{c.purpose}</dd></div>
                  {c.claimId && <div><dt>Claim</dt><dd><code>{c.claimId}</code></dd></div>}
                  {c.denialId && <div><dt>Denial</dt><dd><code>{c.denialId}</code></dd></div>}
                  <div><dt>Duration</dt><dd>{fmtDuration(c.durationSeconds)}</dd></div>
                  {c.startedAt && <div><dt>Started</dt><dd>{c.startedAt}</dd></div>}
                  {c.endedAt && <div><dt>Ended</dt><dd>{c.endedAt}</dd></div>}
                  {c.referenceNumber && <div><dt>Reference</dt><dd><code>{c.referenceNumber}</code></dd></div>}
                  <div><dt>Outcome</dt><dd>{c.outcome}</dd></div>
                </dl>

                {c.recordingUrl && (
                  <div className="pzd-v-record">
                    <div className="pzd-v-label">Recording</div>
                    <div className="pzd-v-audio">
                      <span className="pzd-v-audio-play">▶</span>
                      <div className="pzd-v-audio-scrub">
                        <div className="pzd-v-audio-fill" />
                      </div>
                      <span className="pzd-v-audio-time">{fmtDuration(c.durationSeconds)}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="pzd-vdetail-right">
                {c.transcript && (
                  <div className="pzd-v-panel">
                    <div className="pzd-v-label">Transcript</div>
                    <pre className="pzd-v-transcript">{c.transcript}</pre>
                  </div>
                )}
                {c.aiSummary && (
                  <div className="pzd-v-panel">
                    <div className="pzd-v-label">AI summary</div>
                    <p>{c.aiSummary}</p>
                  </div>
                )}
                {c.functionCalls && c.functionCalls.length > 0 && (
                  <div className="pzd-v-panel">
                    <div className="pzd-v-label">Function calls</div>
                    {c.functionCalls.map((fn, i) => (
                      <pre key={i} className="pzd-v-fn">
{`→ ${fn.fn}(${JSON.stringify(fn.args)})
← ${JSON.stringify(fn.result ?? "(void)")}`}
                      </pre>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function VoiceCallsView() {
  const [expanded, setExpanded] = useState(VOICE_CALLS[0]?.id);
  return (
    <section className="pzd-main">
      <header className="pzd-main-head">
        <div>
          <h1 className="pzd-h1">Voice calls</h1>
          <p className="pzd-h1-sub">AI agent calls to payers for prior-auth, P2P scheduling, and follow-up. Powered by Vapi · Twilio.</p>
        </div>
      </header>

      <div className="pzd-vbar">
        <span className="pzd-vbar-stat"><strong>{VOICE_CALLS.filter(c => c.status === "completed").length}</strong> completed this week</span>
        <span className="pzd-vbar-stat"><strong>{VOICE_CALLS.filter(c => c.status === "in_progress" || c.status === "ringing").length}</strong> live now</span>
        <span className="pzd-vbar-stat"><strong>11:42</strong> avg hold time</span>
        <span className="pzd-vbar-stat"><strong>100%</strong> AI disclosure compliance</span>
      </div>

      <div className="pzd-vtable-wrap">
        <table className="pzd-vtable">
          <thead>
            <tr>
              <th style={{ width: 90 }}>ID</th>
              <th style={{ width: 180 }}>Payer / Claim</th>
              <th style={{ width: 140 }}>Phone</th>
              <th>Purpose</th>
              <th style={{ width: 120 }}>Status</th>
              <th style={{ width: 80, textAlign: "right" }}>Duration</th>
              <th style={{ width: 80 }}>AI disclosed</th>
              <th style={{ width: 150 }}>Started</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {VOICE_CALLS.map((c) => (
              <VoiceRow key={c.id} c={c} expanded={expanded === c.id} onToggle={() => setExpanded(expanded === c.id ? null : c.id)} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────
   Integrations view
   ────────────────────────────────────────────────────────────── */
function IntegrationStatus({ status }) {
  const map = {
    connected: { tone: "green", label: "Connected" },
    attention: { tone: "amber", label: "Needs attention" },
    disconnected: { tone: "red", label: "Disconnected" },
  };
  const s = map[status] || map.connected;
  return <span className={`pzd-intstatus pzd-intstatus--${s.tone}`}><span className="pzd-intstatus-dot" />{s.label}</span>;
}

function IntegrationCard({ item }) {
  return (
    <div className="pzd-intcard">
      <header>
        <h3>{item.name}</h3>
        <IntegrationStatus status={item.status} />
      </header>
      <p className="pzd-intcard-desc">{item.description}</p>
      {item.endpoint && (
        <div className="pzd-intcard-endpoint">
          <span className="pzd-intcard-label">Endpoint</span>
          <code>{item.endpoint}</code>
        </div>
      )}
      {item.scopes && (
        <div className="pzd-intcard-scopes">
          <span className="pzd-intcard-label">Scopes</span>
          <div>
            {item.scopes.map((s) => <code key={s}>{s}</code>)}
          </div>
        </div>
      )}
      {item.lastSync && (
        <div className="pzd-intcard-foot">{item.lastSync}</div>
      )}
      {item.metrics && (
        <div className="pzd-intcard-metrics">
          {item.metrics.map((m) => (
            <div className="pzd-intcard-metric" key={m.label}>
              <div className="pzd-intcard-metric-value">{m.value}</div>
              <div className="pzd-intcard-metric-label">{m.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IntegrationsView() {
  const groups = [
    ["EHR",                  INTEGRATIONS.ehr],
    ["Claims / clearinghouse", INTEGRATIONS.claims],
    ["Payer portals",        INTEGRATIONS.payer],
    ["Fax gateway",          INTEGRATIONS.fax],
    ["Voice · peer-to-peer", INTEGRATIONS.voice],
  ];
  return (
    <section className="pzd-main">
      <header className="pzd-main-head">
        <div>
          <h1 className="pzd-h1">Integrations</h1>
          <p className="pzd-h1-sub">Live connections powering this tenant. Everything is read-only in the demo.</p>
        </div>
      </header>

      {groups.map(([label, items]) => (
        <div className="pzd-intgroup" key={label}>
          <div className="pzd-intgroup-label">{label}</div>
          <div className="pzd-intgrid">
            {items.map((it) => <IntegrationCard key={it.id} item={it} />)}
          </div>
        </div>
      ))}

      <div className="pzd-intgroup">
        <div className="pzd-intgroup-label">Webhooks</div>
        <div className="pzd-intwebhooks">
          {INTEGRATIONS.webhooks.map((w) => (
            <div className="pzd-intwebhook" key={w.event}>
              <span className="pzd-intstatus pzd-intstatus--green"><span className="pzd-intstatus-dot" />{w.status}</span>
              <code className="pzd-intwebhook-event">{w.event}</code>
              <code className="pzd-intwebhook-url">{w.url}</code>
              <span className="pzd-intwebhook-last">last event {w.last}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="pzd-intgroup">
        <div className="pzd-intgroup-label">Active workflows · Temporal</div>
        <div className="pzd-workflows">
          {INTEGRATIONS.workflows.map((w) => (
            <div className={`pzd-workflow pzd-workflow--${w.status}`} key={w.id}>
              <div className="pzd-workflow-head">
                <code>{w.name}</code>
                <span className="pzd-workflow-id">{w.id}</span>
              </div>
              <div className="pzd-workflow-arg">{w.arg}</div>
              <div className="pzd-workflow-step">
                <span className="pzd-workflow-dot" />
                {w.step}
              </div>
              <div className="pzd-workflow-eta">ETA · {w.eta}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────
   Settings view
   ────────────────────────────────────────────────────────────── */
function SettingsView() {
  const [autoSubmit, setAutoSubmit] = useState(SETTINGS.autoSubmit);
  const [notifs, setNotifs] = useState(SETTINGS.notifications);
  const [tone, setTone] = useState(SETTINGS.tone);

  return (
    <section className="pzd-main">
      <header className="pzd-main-head">
        <div>
          <h1 className="pzd-h1">Settings</h1>
          <p className="pzd-h1-sub">Tenant-level configuration for {TENANT.name}.</p>
        </div>
      </header>

      <div className="pzd-setgrid">
        <div className="pzd-setcard">
          <div className="pzd-setcard-head">
            <div>
              <h3>Team</h3>
              <p>Roles granted per user. SSO via Azure AD.</p>
            </div>
            <button className="pzd-btn pzd-btn-ghost pzd-setcard-action">Invite</button>
          </div>
          <table className="pzd-settable">
            <thead>
              <tr><th>Name</th><th>Role</th><th>Email</th><th>Last active</th></tr>
            </thead>
            <tbody>
              {SETTINGS.team.map((u) => (
                <tr key={u.email}>
                  <td>{u.name}</td>
                  <td><span className={`pzd-rolebadge pzd-rolebadge--${u.role.split(" ")[0].toLowerCase()}`}>{u.role}</span></td>
                  <td><code>{u.email}</code></td>
                  <td>{u.active}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pzd-setcard">
          <div className="pzd-setcard-head">
            <div>
              <h3>Auto-submit thresholds</h3>
              <p>Confidence floor to auto-send an appeal without human review, per CARC category.</p>
            </div>
          </div>
          <div className="pzd-slidelist">
            {autoSubmit.map((t) => (
              <div className="pzd-slide" key={t.id}>
                <label>
                  <input
                    type="checkbox"
                    checked={t.enabled}
                    onChange={() => setAutoSubmit(autoSubmit.map((x) => x.id === t.id ? { ...x, enabled: !x.enabled } : x))}
                  />
                  <span>{t.label}</span>
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="0.99"
                  step="0.01"
                  value={t.threshold}
                  onChange={(e) => setAutoSubmit(autoSubmit.map((x) => x.id === t.id ? { ...x, threshold: Number(e.target.value) } : x))}
                  disabled={!t.enabled}
                />
                <span className="pzd-slide-val">{Math.round(t.threshold * 100)}%</span>
              </div>
            ))}
          </div>
        </div>

        <div className="pzd-setcard">
          <div className="pzd-setcard-head">
            <div>
              <h3>Notifications</h3>
              <p>Where PanelZero posts events.</p>
            </div>
          </div>
          <div className="pzd-notifs">
            {notifs.map((n, i) => (
              <label className="pzd-notif" key={n.channel}>
                <input
                  type="checkbox"
                  checked={n.enabled}
                  onChange={() => setNotifs(notifs.map((x, j) => j === i ? { ...x, enabled: !x.enabled } : x))}
                />
                <div>
                  <div className="pzd-notif-channel">{n.channel}</div>
                  <div className="pzd-notif-events">{n.events.join(" · ")}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="pzd-setcard">
          <div className="pzd-setcard-head">
            <div>
              <h3>Appeal tone</h3>
              <p>Controls how the model writes. Applied to every draft.</p>
            </div>
          </div>
          <div className="pzd-tone">
            <div className="pzd-toneline">
              <span>Style</span>
              <div className="pzd-segmented">
                {["formal-clinical","concise-sharp","warm-advocacy"].map((s) => (
                  <button
                    key={s}
                    className={tone.style === s ? "is-on" : ""}
                    onClick={() => setTone({ ...tone, style: s })}
                  >{s}</button>
                ))}
              </div>
            </div>
            <div className="pzd-toneline">
              <span>Length</span>
              <div className="pzd-segmented">
                {["concise","medium","thorough"].map((s) => (
                  <button
                    key={s}
                    className={tone.length === s ? "is-on" : ""}
                    onClick={() => setTone({ ...tone, length: s })}
                  >{s}</button>
                ))}
              </div>
            </div>
            <label className="pzd-togglerow">
              <input type="checkbox" checked={tone.citePolicies} onChange={() => setTone({ ...tone, citePolicies: !tone.citePolicies })} />
              <span>Cite payer policy sections inline (SURG.00092 §4.2, etc.)</span>
            </label>
            <label className="pzd-togglerow">
              <input type="checkbox" checked={tone.includeCPT} onChange={() => setTone({ ...tone, includeCPT: !tone.includeCPT })} />
              <span>Include CPT and ICD-10 codes in narrative</span>
            </label>
          </div>
        </div>

        <div className="pzd-setcard pzd-setcard-wide">
          <div className="pzd-setcard-head">
            <div>
              <h3>Payer-specific templates</h3>
              <p>Version-controlled prompt + formatting bundles per payer. Edits require Admin role.</p>
            </div>
          </div>
          <div className="pzd-templates">
            {SETTINGS.templates.map((t) => (
              <div className={`pzd-template pzd-template--${t.status}`} key={t.payer}>
                <div className="pzd-template-name">{t.payer}</div>
                <div className="pzd-template-meta">
                  <span>v{t.version}</span>
                  <span className={`pzd-template-chip pzd-template-chip--${t.status}`}>{t.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}


/* ──────────────────────────────────────────────────────────────
   Page
   ────────────────────────────────────────────────────────────── */
export default function DemoConsole() {
  const nav = useNavigate();
  // useEffect(() => {
    // if (!isDemoAuthed()) nav("/demo/login", { replace: true });
  // }, [nav]);

  const [view, setView] = useState("denials");
  const [selected, setSelected] = useState(DENIALS.find((d) => d.featured) || DENIALS[0]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [autoPreviewArmed, setAutoPreviewArmed] = useState(true);

  useEffect(() => {
    // Auto-open the featured case once on load so the PDF is immediately visible
    const t = setTimeout(() => setDrawerOpen(true), 650);
    return () => clearTimeout(t);
  }, []);

  const onSelect = (d) => {
    setSelected(d);
    setDrawerOpen(true);
    setAutoPreviewArmed(false);
  };

  const onLogout = () => {
    setDemoAuthed(false);
    nav("/demo/login");
  };

  return (
    <div className="pzd-shell">
      <Sidebar view={view} onChange={setView} onLogout={onLogout} />
      <div className="pzd-content">
        <div className="pzd-topbar">
          <div className="pzd-breadcrumbs">
            <Link to="/" className="pzd-bc-home">panelzero.com</Link>
            <span className="pzd-bc-sep">/</span>
            <span>{TENANT.name}</span>
            <span className="pzd-bc-sep">/</span>
            <span className="pzd-bc-current">{view}</span>
          </div>
          <div className="pzd-topbar-right">
            <span className="pzd-livechip"><span className="pzd-live-dot" /> Live · Epic connected</span>
            <span className="pzd-env">demo-tenant · read-only</span>
          </div>
        </div>

        {view === "denials" && <DenialQueue onSelect={onSelect} selectedId={selected?.id} />}
        {view === "appeals" && <AppealsView onSelect={onSelect} />}
        {view === "faxes" && <FaxesView />}
        {view === "voice" && <VoiceCallsView />}
        {view === "analytics" && <AnalyticsView />}
        {view === "integrations" && <IntegrationsView />}
        {view === "settings" && <SettingsView />}
      </div>

      {drawerOpen && (
        <DenialDrawer
          denial={selected}
          onClose={() => setDrawerOpen(false)}
          autoPreview={autoPreviewArmed && selected?.featured}
          onNavigate={(id) => { setView(id); setDrawerOpen(false); }}
        />
      )}
    </div>
  );
}
